import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { createApi } from '@Pages/pageRuntime/sdk/api';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkSubscriptionPreviewHandle,
  SdkSubscriptionPreviewOptions
} from '@Pages/pageRuntime/sdk/types';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export function useSubscriptionPreview({
  resourceId,
  items,
  discounts,
  startedAt,
  billingAnchorMonth,
  billingAnchorDay,
  billingCycleMode,
  activationPolicy,
  initialTermAnchor,
  initialPaymentWindowDays,
  trialDays,
  trialEndsAt,
  trialPaymentMethodPolicy,
  gracePeriodDays,
  lapseBillingBehavior,
  enabled = true
}: SdkSubscriptionPreviewOptions): SdkSubscriptionPreviewHandle {
  const canPreview = enabled && resourceId.length > 0 && items.length > 0;
  const inputs = {
    items,
    discounts,
    started_at: startedAt,
    billing_anchor_month: billingAnchorMonth,
    billing_anchor_day: billingAnchorDay,
    billing_cycle_mode: billingCycleMode,
    activation_policy: activationPolicy,
    initial_term_anchor: initialTermAnchor,
    initial_payment_window_days: initialPaymentWindowDays,
    trial_days: trialDays,
    trial_ends_at: trialEndsAt,
    trial_payment_method_policy: trialPaymentMethodPolicy,
    grace_period_days: gracePeriodDays,
    lapse_billing_behavior: lapseBillingBehavior
  };
  const query = useQuery<Diagonal.Core.Data.SubscriptionPreviewData, ReturnType<typeof toApiError>>(
    {
      queryKey: [PAGE_API_QUERY_KEY_HEAD, 'records', resourceId, 'subscription-preview', inputs],
      enabled: canPreview,
      retry: false,
      refetchOnMount: 'always',
      staleTime: 0,
      throwOnError: false,
      queryFn: async ({ signal }) => {
        return createApi(axios).post<Diagonal.Core.Data.SubscriptionPreviewData>(
          `/api/data/resources/${encodeURIComponent(resourceId)}/subscription-preview`,
          inputs,
          { signal }
        );
      }
    }
  );
  const isLoading = canPreview && (query.isFetching || query.isPaused);
  const preview = canPreview && !isLoading && !query.isError ? query.data : undefined;

  return {
    preview,
    quote: preview?.quote ?? null,
    isLoading,
    error: canPreview ? query.error : null,
    refetch: async () => {
      if (canPreview) {
        await query.refetch();
      }
    }
  };
}
